import { api } from "./client";

/**
 * The buyer reports surface's own client (WS-B4): the team board and the
 * cost-per-lead report. Deliberately separate from `api/buyers.ts` and
 * `api/advertising.ts` — both are owned elsewhere and this page reads two
 * endpoints those files do not need to know about.
 */

// ---------------------------------------------------------------------------
// Team board — GET /buyer-teams/{id}/board, GET /buyer-teams/board
// ---------------------------------------------------------------------------

export interface BuyerTeamBoardStaleRowDto {
  buyerLeadId: string;
  displayName: string;
  /** The row's name for staff: "{Company} — {Person}" for a company buyer,
   *  else `displayName` (ruling Q5). Always sent by the server; read it as
   *  `staffDisplayName ?? displayName`. The quick-pick's labels keep
   *  `displayName`. */
  staffDisplayName?: string;
  lastContactAtUtc?: string | null;
  /** Server-resolved (`BuyerReassignmentRule`): render the quick-pick only
   *  where this is true, because that is exactly where the server would
   *  accept a change. */
  canReassign: boolean;
  /** Server-resolved (`BuyerReassignmentRule.CanUnassign`): whether the
   *  quick-pick offers "Unassigned (queue)". Optional so older fixtures keep
   *  compiling; the server always sends it. */
  canUnassign?: boolean;
}

export interface BuyerTeamBoardSpecialistDto {
  userId: string;
  displayName: string;
  held: number;
  entitlement: number;
  overdueFollowUps: number;
  staleBuyers: number;
  newThisWeek: number;
  qualifiedThisMonth: number;
  convertedThisMonth: number;
  staleRows: BuyerTeamBoardStaleRowDto[];
}

export interface BuyerTeamBoardDto {
  teamId: string;
  teamName: string;
  /** The window the stale figures were measured against — named on the
   *  surface rather than guessed at. */
  staleContactDays: number;
  specialists: BuyerTeamBoardSpecialistDto[];
}

// ---------------------------------------------------------------------------
// Cost per lead — GET /buyer-advertising/cost-per-lead
// ---------------------------------------------------------------------------

export interface AdvertisingCostPerLeadRowDto {
  advertisementId: string;
  advertisementName: string;
  channel: string;
  buyerTeamId: string;
  buyerTeamName: string;
  /** `YYYY-MM`. */
  period: string;
  spend: number;
  leads: number;
  qualified: number;
  /** Null when the denominator is zero — renders as an em dash, never $0. */
  costPerLead?: number | null;
  costPerQualified?: number | null;
}

export interface AdvertisingBudgetPaceRowDto {
  buyerTeamId: string;
  buyerTeamName: string;
  period: string;
  budget?: number | null;
  spend: number;
  /** A flag, never a block: the spend already happened. False when no budget
   *  is set — there is nothing to be over. */
  isOverBudget: boolean;
}

export interface AdvertisingCostPerLeadDto {
  rows: AdvertisingCostPerLeadRowDto[];
  budgets: AdvertisingBudgetPaceRowDto[];
}

export interface CostPerLeadQuery {
  /** `YYYY-MM`. */
  from: string;
  /** `YYYY-MM`; defaults server-side to `from`. */
  to?: string;
  /** Narrowing only — a one-team caller's own team stands whatever is sent. */
  buyerTeamId?: string;
}

export const buyerReportsApi = {
  board: (teamId: string) =>
    api.get<BuyerTeamBoardDto>(`/buyer-teams/${teamId}/board`),
  /** The caller's own team's board — null when they are on no team. */
  myBoard: () => api.get<BuyerTeamBoardDto | null>("/buyer-teams/board"),
  costPerLead: (query: CostPerLeadQuery) =>
    api.get<AdvertisingCostPerLeadDto>("/buyer-advertising/cost-per-lead", query),
};
